import React from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
} from "@mui/material";

// Keys to completely ignore and not render
const IGNORED_KEYS = ["_id", "id", "__v", "password", "token", "refreshToken", "createdAt", "updatedAt", "createdBy", "createdByUser", "updatedBy", "updatedByUser", "plan", "planType", "customerId", "dealerId", "user", "userId", "prospectId"];

// Helper to format camelCase or snake_case keys into Title Case
const formatLabel = (key) => {
  const result = key.replace(/([A-Z])/g, " $1").replace(/_/g, " ");
  return result.charAt(0).toUpperCase() + result.slice(1).trim();
};

const isDateString = (val) => {
  if (typeof val !== "string") return false;
  // Basic check for ISO 8601 date strings
  const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  return regex.test(val);
};

const isImageUrl = (val) => {
  if (typeof val !== "string") return false;
  return val.match(/\.(jpeg|jpg|gif|png|webp)$/i) !== null || (val.startsWith("http") && val.match(/\.(jpeg|jpg|gif|png|webp)/i));
};

const renderValue = (val) => {
  if (val === null || val === undefined || val === "") return <Typography color="text.secondary">—</Typography>;
  if (typeof val === "boolean") return <Typography>{val ? "Yes" : "No"}</Typography>;
  if (isDateString(val)) return <Typography>{new Date(val).toLocaleString()}</Typography>;
  if (isImageUrl(val)) {
    return (
       <Box 
         component="img" 
         src={val} 
         sx={{ maxWidth: 200, maxHeight: 200, borderRadius: 1, objectFit: "cover", cursor: "pointer", border: "1px solid #ddd", display: 'block', mt: 1 }} 
         onClick={() => window.open(val, '_blank')}
       />
    );
  }
  return <Typography sx={{ wordBreak: "break-word" }}>{String(val)}</Typography>;
};

const RecursiveViewer = ({ data, level }) => {
  if (!data || typeof data !== "object") {
    return renderValue(data);
  }

  // Handle Arrays
  if (Array.isArray(data)) {
    if (data.length === 0) return <Typography color="text.secondary">—</Typography>;
    
    // Check if it's an array of primitives
    const isPrimitiveArray = data.every((item) => typeof item !== "object" || item === null);
    
    if (isPrimitiveArray) {
      return (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
          {data.map((item, index) => {
             const strItem = String(item);
             if (isImageUrl(strItem)) {
               return (
                 <Box 
                   key={index} 
                   component="img" 
                   src={strItem} 
                   sx={{ width: 100, height: 100, borderRadius: 1, objectFit: "cover", cursor: "pointer", border: "1px solid #ddd" }} 
                   onClick={() => window.open(strItem, '_blank')}
                 />
               );
             }
             return <Chip key={index} label={strItem} size="small" variant="outlined" />;
          })}
        </Box>
      );
    }

    // Array of objects
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        {data.map((item, index) => (
          <Card key={index} variant="outlined" sx={{ bgcolor: "background.paper", boxShadow: "none" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
               <Typography variant="subtitle2" color="primary" sx={{ mb: 1.5 }}>
                  Item #{index + 1}
               </Typography>
               <RecursiveViewer data={item} level={level + 1} />
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // Handle Objects
  const keys = Object.keys(data).filter(key => !IGNORED_KEYS.includes(key));
  
  if (keys.length === 0) return <Typography color="text.secondary">—</Typography>;

  return (
    <Grid container spacing={2}>
      {keys.map((key) => {
        const value = data[key];
        const isComplex = typeof value === "object" && value !== null;

        if (isComplex) {
          return (
            <Grid item xs={12} key={key}>
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography 
                  variant="subtitle2" 
                  color="text.secondary" 
                  sx={{ textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: 0.5 }}
                >
                  {formatLabel(key)}
                </Typography>
                <Box sx={{ pl: level === 0 ? 0 : 2, borderLeft: level === 0 ? "none" : "2px solid #e0e0e0", mt: 1 }}>
                  <RecursiveViewer data={value} level={level + 1} />
                </Box>
              </Box>
              {level === 0 && <Divider sx={{ mt: 2 }} />}
            </Grid>
          );
        }

        return (
          <Grid item xs={12} sm={6} md={4} key={key}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {formatLabel(key)}
              </Typography>
              {renderValue(value)}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

const DynamicDataViewer = ({ data }) => {
  if (!data) return <Typography>No data available</Typography>;

  return (
    <Box sx={{ p: 1 }}>
      <RecursiveViewer data={data} level={0} />
    </Box>
  );
};

export default DynamicDataViewer;
